/**
 * columnLetterUtils.js
 * Excel-style column letter <-> index conversion.
 * A=0, B=1, ..., Z=25, AA=26, AB=27, ...
 */

/**
 * Convert a column letter string to a zero-based index.
 * "A" → 0, "Z" → 25, "AA" → 26, "ZZ" → 701
 */
export function columnLetterToIndex(letter) {
  if (!letter || typeof letter !== 'string') return -1;

  const upper = letter.toUpperCase();
  let index = 0;

  for (let i = 0; i < upper.length; i++) {
    index = index * 26 + (upper.charCodeAt(i) - 64);
  }

  return index - 1; // Convert to 0-based
}

/**
 * Convert a zero-based column index to a letter string.
 * 0 → "A", 25 → "Z", 26 → "AA", 701 → "ZZ"
 */
export function columnIndexToLetter(index) {
  if (index < 0) return '';

  let letter = '';
  let n = index + 1; // Convert to 1-based

  while (n > 0) {
    const rem = (n - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    n = Math.floor((n - 1) / 26);
  }

  return letter;
}

/**
 * Get an array of column letters for a given range count starting at offset.
 * getColumnLetters(0, 5) → ["A", "B", "C", "D", "E"]
 */
export function getColumnLetters(startIndex, count) {
  return Array.from({ length: count }, (_, i) => columnIndexToLetter(startIndex + i));
}

/**
 * Check if a string is a valid column letter.
 */
export function isValidColumnLetter(str) {
  return /^[A-Za-z]+$/.test(str);
}

/**
 * Get the column letter for an AG Grid field given column metadata.
 * Returns "A", "B", "C"... based on the field's position in the column array.
 */
export function getColumnLetterForField(field, columns) {
  const idx = columns.findIndex((c) => c.field === field);
  return idx >= 0 ? columnIndexToLetter(idx) : null;
}

/**
 * Build a mapping of { field → columnLetter } for all columns.
 * Used for formula bar display.
 */
export function buildFieldToLetterMap(columns) {
  return columns.reduce((map, col, idx) => {
    map[col.field] = columnIndexToLetter(idx);
    return map;
  }, {});
}

/**
 * Build a mapping of { columnLetter → field } for formula resolution.
 */
export function buildLetterToFieldMap(columns) {
  return columns.reduce((map, col, idx) => {
    map[columnIndexToLetter(idx)] = col.field;
    return map;
  }, {});
}
